import { useState, useEffect, useCallback, useMemo } from 'react';
import { useI18n } from '../i18n.jsx';
import { api } from '../lib/api.js';
import { Activity } from 'lucide-react';

const ActivityLogTab = ({ orgId }) => {
  const { t } = useI18n();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ fromDate: '', toDate: '', fromTime: '', toTime: '' });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api('/audit-logs/');
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setLogs([]);
      setError(err.message || 'Unable to load the activity log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (orgId) {
      fetchLogs();
    } else {
      setLogs([]);
      setLoading(false);
    }
  }, [orgId, fetchLogs]);

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const ts = new Date(log.timestamp);
      const dateOnly = ts.toISOString().slice(0, 10);
      const timeOnly = ts.toTimeString().slice(0, 5);

      if (filters.fromDate && dateOnly < filters.fromDate) return false;
      if (filters.toDate && dateOnly > filters.toDate) return false;
      if (filters.fromTime && timeOnly < filters.fromTime) return false;
      if (filters.toTime && timeOnly > filters.toTime) return false;

      return true;
    });
  }, [logs, filters]);

  const handleDeleteLog = async (logId) => {
    if (!window.confirm(t('deleteLogConfirm') || 'Delete this log entry?')) return;
    try {
      await api(`/audit-logs/${logId}/`, 'DELETE');
      setLogs(prev => prev.filter(log => log.id !== logId));
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to delete the log entry.');
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto', color: 'var(--text-primary)' }}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Activity size={24} style={{ color: 'var(--brand-primary)' }} />
        {t('activityLog') || 'Activity Log'}
      </h2>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '18px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('fromDate') || 'From Date'}</label>
          <input type="date" value={filters.fromDate} onChange={e => setFilters({ ...filters, fromDate: e.target.value })} style={{ padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('toDate') || 'To Date'}</label>
          <input type="date" value={filters.toDate} onChange={e => setFilters({ ...filters, toDate: e.target.value })} style={{ padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('fromTime') || 'From Time'}</label>
          <input type="time" value={filters.fromTime} onChange={e => setFilters({ ...filters, fromTime: e.target.value })} style={{ padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('toTime') || 'To Time'}</label>
          <input type="time" value={filters.toTime} onChange={e => setFilters({ ...filters, toTime: e.target.value })} style={{ padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
        </div>
        <button
          type="button"
          onClick={() => setFilters({ fromDate: '', toDate: '', fromTime: '', toTime: '' })}
          className="btn btn-secondary"
          style={{ alignSelf: 'flex-end' }}
        >
          {t('clearFilters') || 'Clear'}
        </button>
      </div>
      
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
      ) : error ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--brand-danger)' }}>{error}</div>
      ) : filteredLogs.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>No activity found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredLogs.map(log => (
            <div key={log.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '16px', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{log.member_name}</div>
                <button
                  type="button"
                  onClick={() => handleDeleteLog(log.id)}
                  className="btn btn-secondary"
                  style={{ padding: '6px 10px', fontSize: '0.75rem', color: 'var(--brand-danger)' }}
                  title={t('delete') || 'Delete'}
                >
                  {t('delete') || 'Delete'}
                </button>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                {new Date(log.timestamp).toLocaleString()}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ 
                  fontSize: '0.7rem', padding: '3px 8px', borderRadius: '4px', fontWeight: 600,
                  background: log.action === 'CREATE' ? 'rgba(16,185,129,0.1)' : log.action === 'UPDATE' ? 'rgba(56,189,248,0.1)' : 'rgba(239,68,68,0.1)',
                  color: log.action === 'CREATE' ? '#10b981' : log.action === 'UPDATE' ? '#38bdf8' : '#ef4444'
                }}>
                  {log.action}
                </span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  {log.entity_name}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLogTab;
